// Mobile Ads Service for Capacitor (AdMob)
// This service handles native mobile ads for iOS and Android
import { Capacitor } from '@capacitor/core';

// AdMob Configuration
export const ADMOB_CONFIG = {
  publisherId: 'pub-9789426917966477',
  // iOS Ad Unit IDs
  ios: {
    bannerAdUnitId: 'ca-app-pub-9789426917966477/3917070649',
    interstitialAdUnitId: 'ca-app-pub-9789426917966477/8213641945',
  },
  // Android Ad Unit IDs
  android: {
    bannerAdUnitId: 'ca-app-pub-9789426917966477/8172010426',
    interstitialAdUnitId: 'ca-app-pub-9789426917966477/3169198826',
  },
  // Test Ad Unit IDs (use these during development)
  test: {
    bannerAdUnitId: 'ca-app-pub-3940256099942544/6300978111',
    interstitialAdUnitId: 'ca-app-pub-3940256099942544/1033173712',
  },
};

class MobileAdService {
  constructor() {
    this.isNative = Capacitor.isNativePlatform();
    this.platform = Capacitor.getPlatform(); // 'ios', 'android', or 'web'
    this.isInitialized = false;
    this.AdMob = null;
  }

  getAdUnitId(type) {
    // Return platform-specific ad unit ID
    if (this.platform === 'ios') {
      return type === 'banner' ? ADMOB_CONFIG.ios.bannerAdUnitId : ADMOB_CONFIG.ios.interstitialAdUnitId;
    } else if (this.platform === 'android') {
      return type === 'banner' ? ADMOB_CONFIG.android.bannerAdUnitId : ADMOB_CONFIG.android.interstitialAdUnitId;
    }
    return null;
  }

  async initialize() {
    if (!this.isNative || this.isInitialized) {
      return;
    }

    try {
      const { AdMob } = await import('@capacitor-community/admob');
      this.AdMob = AdMob;

      await AdMob.initialize({
        requestTrackingAuthorization: true,
        testingDevices: [], // Add your test device IDs here during development
        initializeForTesting: false, // Set to true during development
      });

      this.isInitialized = true;
      console.log('AdMob initialized successfully for platform:', this.platform);
    } catch (error) {
      console.error('Failed to initialize AdMob:', error);
    }
  }

  async showBannerAd(position = 'BOTTOM_CENTER') {
    if (!this.isNative || !this.isInitialized || !this.AdMob) {
      return;
    }

    try {
      const { BannerAdSize, BannerAdPosition } = await import('@capacitor-community/admob');
      const adUnitId = this.getAdUnitId('banner');
      
      if (!adUnitId) {
        console.warn('No banner ad unit ID for platform:', this.platform);
        return;
      }
      
      await this.AdMob.showBanner({
        adId: adUnitId,
        adSize: BannerAdSize.ADAPTIVE_BANNER,
        position: position === 'TOP' ? BannerAdPosition.TOP_CENTER : BannerAdPosition.BOTTOM_CENTER,
        margin: 0,
        isTesting: false, // Set to true during development
      });
      
      console.log('Banner ad shown with ID:', adUnitId);
    } catch (error) {
      console.error('Failed to show banner ad:', error);
    }
  }

  async hideBannerAd() {
    if (!this.isNative || !this.AdMob) {
      return;
    }

    try {
      await this.AdMob.hideBanner();
    } catch (error) {
      console.error('Failed to hide banner:', error);
    }
  }

  async prepareInterstitialAd() {
    if (!this.isNative || !this.isInitialized || !this.AdMob) {
      return false;
    }

    try {
      const adUnitId = this.getAdUnitId('interstitial');
      
      if (!adUnitId) {
        console.warn('No interstitial ad unit ID for platform:', this.platform);
        return false;
      }
      
      await this.AdMob.prepareInterstitial({
        adId: adUnitId,
        isTesting: false, // Set to true during development
      });
      console.log('Interstitial ad prepared with ID:', adUnitId);
      return true;
    } catch (error) {
      console.error('Failed to prepare interstitial:', error);
      return false;
    }
  }

  async showInterstitialAd() {
    if (!this.isNative || !this.isInitialized || !this.AdMob) {
      return false;
    }

    try {
      await this.AdMob.showInterstitial();
      console.log('Interstitial ad shown');
      return true;
    } catch (error) {
      console.error('Failed to show interstitial:', error);
      return false;
    }
  }

  // Call this on natural transition points (e.g., after reading 5 surahs)
  async maybeShowInterstitial(surahCount) {
    // Show interstitial every 5 surahs viewed
    if (surahCount > 0 && surahCount % 5 === 0) {
      const prepared = await this.prepareInterstitialAd();
      if (prepared) {
        // Small delay before showing
        setTimeout(() => this.showInterstitialAd(), 500);
      }
    }
  }
}

export const mobileAdService = new MobileAdService();
export default mobileAdService;
